import { useCallback, useMemo, useState } from "react";

import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import {
  MappedAllAssessmentTableRow,
  getAssessmentStatusLabel,
} from "components/materials/section/assessment/utils";
import { SortDirection } from "mds/components/SortableTable";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentSectionId } from "store/selectors";
import { selectAllAssessmentInfo } from "store/selectors/computed";
import { useQueryParamsManager } from "utils/routing";

export const useIsAssessing = () => {
  const { getQueryParam } = useQueryParamsManager();
  const { canAuthorSection } = useCapabilities();

  if (!canAuthorSection) {
    return false;
  }
  // Instructors can assess, so check the query param
  // to see if they are currently assessing
  const isAssessing = getQueryParam(ASSESSMENT_VIEW_QUERY_PARAM);
  return !!isAssessing;
};

export type AllAssessmentDashboardDataType = {
  allAssessmentsCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  allAssessmentsTableRows: MappedAllAssessmentTableRow[];
};

export type AssessmentStatusTableSortField =
  | "topic"
  | "page"
  | "status"
  | "due_date"
  | "publishing"
  | "submissions"
  | "progress"
  | "assessment_category"
  | "avg_points"
  | "outcomes"
  | "last_submitted"
  | "last_assessed";

const getSortValue = (
  row: MappedAllAssessmentTableRow,
  field: AssessmentStatusTableSortField,
): number | string => {
  switch (field) {
    case "topic":
      return row.topicTitle ?? "";
    case "page":
      return row.title;
    case "status":
      return getAssessmentStatusLabel(
        row.isMarkedComplete,
        row.assessedSubmissionsCount,
        row.isAssessmentComplete,
      );
    case "due_date":
      return row.dueAtRaw ?? "";
    case "publishing":
      return row.publishStatus;
    case "submissions":
      return row.totalSubmissionsCount;
    case "progress": {
      if (row.totalSubmissionsCount === 0) return -1;
      return row.assessedSubmissionsCount / row.totalSubmissionsCount;
    }
    case "assessment_category":
      return row.assessmentCategory;
    case "avg_points": {
      const parsed = parseFloat(row.avgPoints);
      return isNaN(parsed) ? -1 : parsed;
    }
    case "outcomes":
      return row.outcomesCount;
    case "last_submitted":
      return row.lastSubmittedRaw ?? "";
    case "last_assessed":
      return row.lastAssessedRaw ?? "";
    default:
      return 0;
  }
};

export const useSortableRows = (
  rows: MappedAllAssessmentTableRow[],
  defaultSortField?: AssessmentStatusTableSortField,
  defaultSortDirection: SortDirection = "desc",
) => {
  const [sortField, setSortField] = useState<AssessmentStatusTableSortField | null>(
    defaultSortField ?? null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    defaultSortField ? defaultSortDirection : "asc",
  );

  const handleSort = useCallback(
    (field: AssessmentStatusTableSortField) => {
      if (sortField === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    },
    [sortField],
  );

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      let cmp = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        cmp = aVal.localeCompare(bVal);
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        cmp = aVal - bVal;
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [rows, sortField, sortDirection]);

  return { sortedRows, sortField, sortDirection, handleSort };
};

export const useLateSubmissionCounts = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const allAssessmentInfo = useAppSelector(selectAllAssessmentInfo);
  const pages = useAppSelector(pageSelectors.selectEntities);

  return useMemo(() => {
    let totalLateCount = 0;
    let newLateCount = 0;

    for (const pageId of Object.keys(allAssessmentInfo)) {
      const info = allAssessmentInfo[pageId];
      const page = pages[pageId];
      if (page && !page.assessing_marked_complete_at && page.section_id === sectionId) {
        totalLateCount += info.unassessed_late_submissions_count;
        newLateCount += info.unseen_late_submissions_count;
      }
    }

    return { totalLateCount, newLateCount };
  }, [allAssessmentInfo, pages, sectionId]);
};
